<script lang="ts">
	import { onNavigate } from '$app/navigation';
	import type { dbOrderUnavailableDatesType } from '$lib/db/connections.server';
	import Holidays from 'date-holidays';
	import { cart } from '$stores/cart';

	export let chosenDate: Date | undefined;
	let dialog: HTMLDialogElement | undefined;
	let monthDetails: HTMLDetailsElement;
	let yearDetails: HTMLDetailsElement;
	let usersYear = new Date().getFullYear();
	let daysIndex: { [key: number]: number } = {
		1: 0,
		2: 1,
		3: 2,
		4: 3,
		5: 4,
		6: 5,
		0: 6
	};
	let daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
	let monthDays: Array<
		Array<{ text: string; available: boolean; tooltip: string }>
	> = [];
	let monthSelect: HTMLSelectElement;
	let unavailableDates: Array<any>;
	let dbUnavailableDatesUnfiltered: Array<any> = [];
	let dbUnavailableDates: Array<any> = [];
	let loading: boolean = true;
	getDbUnavailableDates();
	let availableMonths: Record<number, string> = generateMonths();
	let usersMonth: number = Number(Object.keys(availableMonths)[0]);

	async function getDbUnavailableDates() {
		let response = await fetch('/api/getUnavailableDates', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		response = await response.json();

		if (response.status === 200) {
			dbUnavailableDatesUnfiltered =
				response.body as unknown as dbOrderUnavailableDatesType[];
		} else {
			dbUnavailableDatesUnfiltered = [];
		}
		loading = false;
	}

	function getManualUnavailableDates() {
		let tempDbUnavailableDates = dbUnavailableDatesUnfiltered.filter((date) => {
			if (
				date.year == usersYear &&
				(($cart.delivery === true && date.delivery === false) ||
					($cart.delivery === false && date.collection === false))
			) {
				return true;
			} else {
				return false;
			}
		});
		dbUnavailableDates = tempDbUnavailableDates.map((tempDateObject) => {
			return {
				date:
					tempDateObject.year +
					'-' +
					tempDateObject.month +
					'-' +
					tempDateObject.day +
					' 00:00:00',
				type: 'manual',
				name: checkTempDateObject(tempDateObject)
			};
		});
		return dbUnavailableDates;
	}

	function checkTempDateObject(tempDateObject: dbOrderUnavailableDatesType) {
		if (
			tempDateObject.delivery === false &&
			tempDateObject.collection === false
		) {
			return 'Unavailable';
		} else if (tempDateObject.delivery === false) {
			return 'Delivery unavailable';
		} else if (tempDateObject.collection === false) {
			return 'Collection unavailable';
		}
	}

	$: if (!loading) {
		unavailableDates = getUnavailableDates();
		dbUnavailableDates = getManualUnavailableDates();

		if (dbUnavailableDates.length > 0) {
			unavailableDates = unavailableDates.concat(dbUnavailableDates);
		}

		monthDays = Array.from({ length: 6 }, () =>
			Array.from(
				{ length: 7 },
				() =>
					new Object({ text: '', available: false, tooltip: '' }) as {
						text: string;
						available: boolean;
						tooltip: string;
					}
			)
		);
		let tempDay = 1;
		let tempDate = new Date().setDate(new Date().getDate() + 2);
		for (
			let i = daysIndex[new Date(usersYear, usersMonth, 1).getDay()];
			i < 7;
			i++
		) {
			if (
				new Date(usersYear + '/' + (usersMonth + 1) + '/' + tempDay) <
				new Date(tempDate)
			) {
				monthDays[0][i] = {
					text: tempDay.toString(),
					available: false,
					tooltip: ''
				};
			} else {
				let dateAvailability = checkDateAvailability(
					usersYear,
					usersMonth,
					tempDay
				);
				monthDays[0][i] = {
					text: tempDay.toString(),
					available: dateAvailability.available,
					tooltip: dateAvailability.tooltip as string
				};
			}
			tempDay++;
		}
		for (let i = 1; i < 6; i++) {
			for (let j = 0; j < 7; j++) {
				if (tempDay <= daysInMonths[usersMonth]) {
					if (
						new Date(usersYear + '/' + (usersMonth + 1) + '/' + tempDay) <
						new Date(tempDate)
					) {
						monthDays[i][j] = {
							text: tempDay.toString(),
							available: false,
							tooltip: 'Unavailable'
						};
					} else {
						let dateAvailability = checkDateAvailability(
							usersYear,
							usersMonth,
							tempDay
						);
						monthDays[i][j] = {
							text: tempDay.toString(),
							available: dateAvailability.available,
							tooltip: dateAvailability.tooltip
						};
					}
					tempDay++;
				}
			}
		}
		let removeLastRow = true;
		for (let i = 0; i < 7; i++) {
			if (monthDays[5][i].text != '') {
				removeLastRow = false;
			}
		}
		if (removeLastRow) {
			monthDays.pop();
		}
	}

	function checkDateAvailability(year: number, month: number, day: number) {
		let date =
			year +
			'-' +
			(month + 1).toString().padStart(2, '0') +
			'-' +
			day.toString().padStart(2, '0');
		let dateName = unavailableDates.find(
			(tempDate) =>
				tempDate.date.split(' ')[0] === date &&
				(tempDate.type === 'bank' ||
					tempDate.type === 'public' ||
					tempDate.type === 'manual')
		)?.name;

		//Checks if the date is a Sunday
		let dayOfWeek = new Date(year, month, day).getDay();
		if (dayOfWeek === 0) {
			return {
				name: day,
				available: false,
				tooltip: 'Unavailable'
			};
		}

		return {
			name: day,
			available: dateName === undefined ? true : false,
			tooltip: dateName || 'Unavailable'
		};
	}

	function toggleMonthDetails() {
		monthDetails.open = !monthDetails.open;
		if (monthDetails.open === true && yearDetails.open === true)
			yearDetails.open = false;
	}

	function toggleYearDetails() {
		yearDetails.open = !yearDetails.open;
		if (yearDetails.open === true && monthDetails.open === true)
			monthDetails.open = false;
	}

	$: {
		// Check for leap year
		if ((usersYear % 4 == 0 && usersYear % 100 != 0) || usersYear % 400 == 0) {
			daysInMonths[1] = 29;
		} else {
			daysInMonths[1] = 28;
		}
	}

	onNavigate(() => {
		if (dialog?.open) {
			dialog.close();
		}
	});

	function openDialog() {
		if (dialog) dialog.showModal();
	}

	function closeDialog() {
		if (dialog) {
			dialog.close();
		}
	}

	function checkDate(year: number, month: number, day: string) {
		const UTCTimeStamp = Date.UTC(year, month, parseInt(day), 0, 0, 0, 0);
		chosenDate = new Date(UTCTimeStamp);
		closeDialog();
	}

	$: {
		const minDate = new Date();
		minDate.setDate(minDate.getDate() + 2);
		const minMonth = minDate.getMonth();
		const minYear = minDate.getFullYear();

		if (usersYear === minYear && usersMonth < minMonth) {
			usersMonth = minMonth;
		}
	}

	function changeMonth(direction: number) {
		const monthKeys = Object.keys(availableMonths).map(Number);
		let currentIndex = monthKeys.indexOf(usersMonth);
		if (currentIndex === -1) {
			usersMonth = monthKeys[0];
			return;
		}

		currentIndex += direction;
		if (currentIndex < 0) {
			currentIndex = monthKeys.length - 1;
			usersYear -= 1;
		} else if (currentIndex >= monthKeys.length) {
			currentIndex = 0;
			usersYear += 1;
		}
		usersMonth = monthKeys[currentIndex];
	}

	function getUnavailableDates() {
		let hd = new Holidays();
		hd.init('IE');
		let holidays = hd.getHolidays(usersYear);
		return holidays;
	}

	function closeDialogWhenClickedOutside(
		event: MouseEvent,
		clickedDialog: HTMLDialogElement | undefined
	) {
		if (event.target === clickedDialog) {
			clickedDialog.close();
		}
	}

	function generateMonths() {
		let tempMonthList = Array.from({ length: 12 }, (_, i) => ({
			month: i,
			name: new Date(0, i).toLocaleString('default', { month: 'long' })
		}));

		tempMonthList = tempMonthList.filter((month) => {
			if (
				month.month >= new Date().getMonth() &&
				usersYear == new Date().getFullYear()
			) {
				return true;
			} else if (usersYear > new Date().getFullYear()) {
				return true;
			} else {
				return false;
			}
		});

		tempMonthList = tempMonthList.filter((month) =>
			checkFulfilmentDates(usersYear, month.month)
		);

		return tempMonthList.reduce(
			(acc, month) => {
				acc[month.month] = month.name;
				return acc;
			},
			{} as Record<number, string>
		);
	}

	$: {
		$cart.items.length;
		availableMonths = generateMonths();
	}

	$: {
		availableMonths;
		if (!(usersMonth in availableMonths)) {
			usersMonth = Number(Object.keys(availableMonths)[0]);
		}
	}

	$: {
		usersYear;
		availableMonths = generateMonths();
	}

	function checkFulfilmentDates(
		year: number | undefined,
		month: number | undefined
	) {
		if (year === undefined || month === undefined || !$cart.fulfilmentDates) {
			return true;
		}

		const userSelectedDate = new Date(year, month);
		let tempStartDate: Date | undefined = undefined;
		let tempEndDate: Date | undefined = undefined;
		if ($cart.fulfilmentDates.start.split('-').length > 2) {
			tempStartDate = new Date($cart.fulfilmentDates.start);
		} else {
			tempStartDate = new Date(
				year,
				parseInt($cart.fulfilmentDates.start.split('-')[0]) - 1,
				parseInt($cart.fulfilmentDates.start.split('-')[1])
			);
		}
		if ($cart.fulfilmentDates.end.split('-').length > 2) {
			tempEndDate = new Date($cart.fulfilmentDates.end);
		} else {
			tempEndDate = new Date(
				year,
				parseInt($cart.fulfilmentDates.end.split('-')[0]) - 1,
				parseInt($cart.fulfilmentDates.end.split('-')[1])
			);
		}

		if (userSelectedDate >= tempStartDate && userSelectedDate <= tempEndDate) {
			return true;
		} else {
			return false;
		}
	}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<dialog
	bind:this={dialog}
	class="space-y-2 rounded-lg border border-gray-200 p-4 text-sm shadow-lg"
	on:click={(event) => closeDialogWhenClickedOutside(event, dialog)}
>
	<div class="flex justify-end">
		<button
			aria-label="Close date picker"
			on:click={() => {
				if (dialog) dialog.close();
			}}
			class="text-base text-logoBackgroundBlue hover:text-logobackgroundBlueRgba"
			><span class="fa-duotone fa-circle-xmark"></span></button
		>
	</div>

	<div class="flex justify-between">
		<button
			on:click={() => changeMonth(-1)}
			disabled={usersMonth == Number(Object.keys(availableMonths)[0]) &&
				usersYear == new Date().getFullYear()}
			aria-label="Previous Month"
			class="text-lg font-medium text-logoBackgroundBlue disabled:text-gray-300"
		>
			<span class="fa-solid fa-angle-left"></span>
		</button>

		<div class="flex gap-1">
			<details class="relative" bind:this={monthDetails}>
				<summary
					on:click|preventDefault={toggleMonthDetails}
					style="-webkit-details-marker: none; list-style-type: none;"
					class="text-lg font-medium text-gray-900 hover:text-gray-600"
					>{availableMonths[usersMonth]}</summary
				>
				<section class="absolute z-10 rounded border border-gray-300">
					<fieldset
						class="flex max-h-[25vh] flex-col justify-between space-y-2 overflow-auto rounded bg-white p-2"
					>
						{#each Object.entries(availableMonths) as [key, month] (key)}
							<div class="flex gap-2">
								<button
									aria-current={usersMonth == Number(key)}
									on:click={() => {
										usersMonth = Number(key);
										toggleMonthDetails();
										//Sets focus to the summary element
										monthDetails.querySelector('summary')?.focus();
									}}>{month}</button
								>
								{#if usersMonth == Number(key)}
									<span
										class="fa-solid fa-check my-auto text-logoBackgroundBlue"
									/>
								{/if}
							</div>
						{/each}
					</fieldset>
				</section>
			</details>
			<span class="text-lg font-medium text-gray-900">/</span>
			<details class="relative" bind:this={yearDetails}>
				<summary
					on:click|preventDefault={toggleYearDetails}
					style="-webkit-details-marker: none; list-style-type: none;"
					class="text-lg font-medium text-gray-900 hover:text-gray-600"
					>{usersYear}</summary
				>

				<section class="absolute z-10 rounded border border-gray-300">
					<fieldset
						class="flex max-h-[25vh] flex-col justify-between space-y-2 overflow-auto rounded bg-white p-2"
					>
						{#each Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i) as year}
							<div class="flex gap-2">
								<button
									aria-current={usersYear == year}
									on:click={() => {
										usersYear = year;
										toggleYearDetails();
										//Sets focus to the summary element
										yearDetails.querySelector('summary')?.focus();
									}}>{year}</button
								>
								{#if usersYear == year}
									<span
										class="fa-solid fa-check my-auto text-logoBackgroundBlue"
									/>
								{/if}
							</div>
						{/each}
					</fieldset>
				</section>
			</details>
		</div>

		<button
			on:click={() => changeMonth(1)}
			disabled={usersMonth ==
				Number(
					Object.keys(availableMonths)[Object.keys(availableMonths).length - 1]
				)}
			aria-label="Next Month"
			class="text-lg font-medium text-logoBackgroundBlue disabled:text-gray-300"
		>
			<span class="fa-solid fa-angle-right"></span>
		</button>
	</div>

	<table class="border-separate border-spacing-4">
		<thead aria-label="Days of the week" class="text-gray-500">
			<tr>
				<th class="font-normal" scope="col">Mo</th>
				<th class="font-normal" scope="col">Tu</th>
				<th class="font-normal" scope="col">We</th>
				<th class="font-normal" scope="col">Th</th>
				<th class="font-normal" scope="col">Fr</th>
				<th class="font-normal" scope="col">Sa</th>
				<th class="font-normal" scope="col">Su</th>
			</tr>
		</thead>
		<tbody
			class="text-center"
			aria-label={'Calendar for ' +
				new Date(0, usersMonth).toLocaleString('default', { month: 'long' }) +
				' ' +
				usersYear}
		>
			{#each monthDays as week}
				<tr>
					{#each week as day}
						{#if day.available}
							{#if chosenDate == new Date(usersYear, usersMonth, parseInt(day.text))}
								<td
									aria-current="date"
									on:click={() => checkDate(usersYear, usersMonth, day.text)}
									class="rounded-full ring-1 ring-inset ring-logoBackgroundBlue"
									>{day.text}</td
								>
							{:else}
								<td
									class="cursor-pointer"
									on:click={() => checkDate(usersYear, usersMonth, day.text)}
									>{day.text}</td
								>
							{/if}
						{:else}
							<td
								aria-disabled="true"
								title={day.tooltip}
								class="cursor-not-allowed text-gray-300">{day.text}</td
							>
						{/if}
					{/each}
				</tr>
			{/each}
		</tbody>
	</table>
</dialog>

<button
	aria-haspopup="dialog"
	class="inline-block text-sm text-logoBackgroundBlue underline underline-offset-4 transition hover:text-logobackgroundBlueRgba"
	aria-label={chosenDate
		? `Change ${$cart.delivery ? 'delivery' : 'collection'} date`
		: `Choose a ${$cart.delivery ? 'delivery' : 'collection'} date`}
	on:click={() => openDialog()}
	>{chosenDate
		? `Change ${$cart.delivery ? 'delivery' : 'collection'} date `
		: `Choose a ${$cart.delivery ? 'delivery' : 'collection'} date `}<span
		class="fa-duotone fa-calendars"
	></span></button
>
